import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight, Building2, MessageSquare, Users, Sparkles, Shield, Check,
  Award, Star, Newspaper, BookOpen,
} from "lucide-react";
import SEO, { ORG_SCHEMA, WEBSITE_SCHEMA } from "@/components/SEO";
import PayHeatmap from "@/components/PayHeatmap";
import { track } from "@/lib/analytics";
import { api } from "@/lib/api";

const ticker = [
  "VERIFIED NURSES ONLY",
  "NO RECRUITERS · NO SPAM · NURSE-OWNED",
];

const TRUST_POINTS = [
  { label: "Verified nurses only" },
  { label: "No recruiters" },
  { label: "No spam" },
  { label: "Nurse-owned" },
];

export default function Landing() {
  const [stats, setStats] = useState(null);
  const [founding, setFounding] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try { const { data } = await api.get("/stats/hero"); if (!cancelled) setStats(data); }
      catch { /* silent — tiles fall back to labels only */ }
      try { const { data } = await api.get("/founding/stats"); if (!cancelled) setFounding(data); }
      catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);
  const fmt = (n) => (n == null ? "—" : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n));
  const remaining = founding?.remaining ?? 30;
  const claimed = founding?.claimed ?? 0;
  return (
    <div data-testid="landing-page">
      <SEO
        title=""
        path="/"
        description="The only nurse-owned, verified network built exclusively for licensed U.S. nurses. Compare agencies, build a verified profile, ask career questions, discuss nursing, stay informed — all in one place."
        jsonLd={[ORG_SCHEMA, WEBSITE_SCHEMA]}
      />

      {/* HERO */}
      <section className="relative border-b-2 border-black nh-grid-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-14 pb-20">
          <div className="grid lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-8">
              <Badge className="bg-black text-white rounded-none border-2 border-black uppercase tracking-widest text-xs" data-testid="hero-badge">
                Nurse-owned · U.S. wide
              </Badge>
              <p className="mt-3 text-xs uppercase tracking-widest text-[hsl(var(--nh-red))] font-black" data-testid="brand-promise">
                The professional home for nurses.
              </p>
              <h1 className="mt-6 text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-black leading-[0.9] tracking-tighter">
                Built by nurses.
                <br />
                <span className="inline-block bg-[hsl(var(--nh-red))] text-white px-3 -rotate-1">Built for every nurse.</span>
              </h1>
              <p className="mt-8 max-w-2xl text-lg text-black/80" data-testid="hero-subheading">
                The only place where nurses can compare agencies, build a verified profile, ask career questions, discuss nursing, and stay informed—all in one place.
              </p>

              {/* Trust badges — ✔ Verified nurses only · No recruiters · No spam · Nurse-owned */}
              <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm font-black" data-testid="trust-points">
                {TRUST_POINTS.map(t => (
                  <li key={t.label} className="inline-flex items-center gap-2">
                    <Check className="w-5 h-5 text-[hsl(var(--nh-red))]" strokeWidth={3} />
                    <span>{t.label}</span>
                  </li>
                ))}
              </ul>

              {/* Founder credit — subtle, adds instant human credibility above the fold */}
              <Link to="/founding-nurses" data-testid="founder-credit"
                className="mt-4 group inline-flex items-center gap-3 border-2 border-black bg-white/70 pl-1 pr-3 py-1 nh-hover-lift">
                <img
                  src="https://customer-assets-cm19k8pv.emergentagent.net/job_nurselink-7/artifacts/0o20uvyx_Snapchat-1914770736.webp"
                  alt="Ashley — Founder of NurseLinkHub, LPN"
                  className="w-9 h-9 object-cover border-2 border-black"
                  loading="eager"
                  fetchpriority="high"
                />
                <div className="text-xs leading-tight">
                  <div className="font-black">Built by a nurse. Backed by nurses.</div>
                  <div className="text-black/60">
                    Meet Ashley, LPN — founder <span className="text-[hsl(var(--nh-red))] font-black group-hover:underline underline-offset-4">→</span>
                  </div>
                </div>
              </Link>

              {/* FOUNDING 30 CTA — primary */}
              <div className="mt-8 bg-[hsl(var(--nh-yellow))] nh-brutal p-6" data-testid="founding-cta">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest">
                  <Award className="w-4 h-4" strokeWidth={3} />
                  <span className="font-black">Founding 30 · Limited spots</span>
                </div>
                <h2 className="mt-2 text-3xl sm:text-4xl font-black tracking-tighter">
                  Become one of the Founding 30 Nurses.
                </h2>
                <p className="mt-3 text-black/80 max-w-2xl">
                  Help build the first verified nationwide community <strong>built by nurses, for nurses.</strong>
                  The first 30 verified nurses receive <strong>Founding Nurse status</strong> and <strong>complimentary 1-year membership</strong>.
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-4">
                  <div className="text-4xl font-black tabular-nums leading-none" data-testid="founding-remaining">
                    {remaining}<span className="text-lg text-black/60 font-bold ml-2">Founding Nurse spots available</span>
                  </div>
                </div>
                <div className="mt-5 flex flex-wrap gap-3">
                  <Link to="/register" data-testid="hero-cta-signup" onClick={() => track("cta_click", { label: "landing_founding_join" })}>
                    <Button className="bg-black hover:bg-black/90 text-white rounded-none border-2 border-black h-12 px-6 text-base nh-hover-push">
                      Claim your Founding Nurse spot <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                  </Link>
                  <Link to="/story" data-testid="hero-cta-why" onClick={() => track("cta_click", { label: "landing_why_built" })}>
                    <Button variant="outline" className="rounded-none border-2 border-black h-12 px-6 text-base nh-hover-push bg-white">
                      Why We Built This
                    </Button>
                  </Link>
                </div>
                <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-sm text-black/80" data-testid="founding-perks">
                  <li className="inline-flex items-center gap-2"><Star className="w-4 h-4" strokeWidth={2.5} /> Founding Nurse badge on your profile</li>
                  <li className="inline-flex items-center gap-2"><Star className="w-4 h-4" strokeWidth={2.5} /> Complimentary 1-year membership</li>
                  <li className="inline-flex items-center gap-2"><Star className="w-4 h-4" strokeWidth={2.5} /> Listed permanently on the Founding Nurses page</li>
                  <li className="inline-flex items-center gap-2"><Star className="w-4 h-4" strokeWidth={2.5} /> Early access to new features</li>
                  <li className="inline-flex items-center gap-2"><Star className="w-4 h-4" strokeWidth={2.5} /> Vote on the future of NurseLinkHub</li>
                </ul>
              </div>

              <div className="mt-4 text-sm">
                <Link to="/trust" data-testid="hero-trust-link" className="underline underline-offset-4 hover:text-[hsl(var(--nh-red))]">See the receipts → live network stats</Link>
              </div>
            </div>

            <div className="lg:col-span-4 lg:mt-2">
              {/* Everything-nursing collage — 6 tiles linking to the pillars of the platform */}
              <div className="grid grid-cols-2 gap-3" data-testid="hero-collage">
                <Link to="/nurses" data-testid="collage-nurses" className="nh-brutal bg-[hsl(var(--nh-yellow))] p-4 nh-hover-lift block">
                  <Users className="w-6 h-6" />
                  <div className="mt-3 text-2xl font-black tabular-nums leading-none">{fmt(stats?.nurses)}</div>
                  <div className="mt-1 text-xs uppercase tracking-widest opacity-70">Nurse profiles</div>
                  <div className="text-sm font-bold">Verified nurses</div>
                </Link>
                <Link to="/feed" data-testid="collage-community" className="nh-brutal bg-white p-4 nh-hover-lift block">
                  <MessageSquare className="w-6 h-6 text-[hsl(var(--nh-red))]" />
                  <div className="mt-3 text-2xl font-black tabular-nums leading-none">{fmt(stats?.posts_week)}</div>
                  <div className="mt-1 text-xs uppercase tracking-widest opacity-70">Community · 7d</div>
                  <div className="text-sm font-bold">Shop-talk feed</div>
                </Link>
                <Link to="/forums" data-testid="collage-forums" className="nh-brutal bg-[hsl(var(--nh-mint))] p-4 nh-hover-lift block">
                  <Users className="w-6 h-6" />
                  <div className="mt-3 text-2xl font-black tabular-nums leading-none">{fmt(stats?.threads)}</div>
                  <div className="mt-1 text-xs uppercase tracking-widest opacity-70">Forums</div>
                  <div className="text-sm font-bold">Ask & answer</div>
                </Link>
                <Link to="/news" data-testid="collage-news" className="nh-brutal bg-white p-4 nh-hover-lift block">
                  <Newspaper className="w-6 h-6" />
                  <div className="mt-3 text-2xl font-black tabular-nums leading-none">{fmt(stats?.news_week)}</div>
                  <div className="mt-1 text-xs uppercase tracking-widest opacity-70">News · 7d</div>
                  <div className="text-sm font-bold">Stay informed</div>
                </Link>
                <Link to="/skills" data-testid="collage-skills" className="nh-brutal bg-[hsl(var(--nh-red))] text-white p-4 nh-hover-lift block">
                  <BookOpen className="w-6 h-6" />
                  <div className="mt-3 text-2xl font-black tabular-nums leading-none">{fmt(stats?.skills)}</div>
                  <div className="mt-1 text-xs uppercase tracking-widest opacity-70">Education</div>
                  <div className="text-sm font-bold">Skills & CEUs</div>
                </Link>
                <Link to="/agencies" data-testid="collage-agencies" className="nh-brutal bg-white p-4 nh-hover-lift block">
                  <Building2 className="w-6 h-6" />
                  <div className="mt-3 text-2xl font-black tabular-nums leading-none">{fmt(stats?.agencies)}</div>
                  <div className="mt-1 text-xs uppercase tracking-widest opacity-70">Agencies</div>
                  <div className="text-sm font-bold">Every U.S. agency</div>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Ticker */}
        <div className="border-t-2 border-black bg-[hsl(var(--nh-yellow))] overflow-hidden">
          <div className="flex nh-marquee whitespace-nowrap py-3 font-black text-xl tracking-tight">
            {[...ticker, ...ticker, ...ticker, ...ticker].map((t, i) => (
              <span key={i} className="mx-8">{t} ✦</span>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURE GRID */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <div className="mb-10 flex items-end justify-between flex-wrap gap-4">
          <h2 className="text-4xl sm:text-5xl font-black tracking-tighter">Built for working nurses.</h2>
          <div className="text-sm text-black/60">Four things. Done right.</div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: Building2,   title: "Agency Marketplace",     desc: "Every U.S. nursing agency. Filter by pay, state, RN/LPN, travel, contract type. Direct application links.", color: "nh-brutal-red",    to: "/agencies", testid: "feature-agencies" },
            { icon: Users,       title: "Nurse Portfolios",       desc: "Show off your license, specialties, experience, and posts. Your professional home on the internet.",       color: "nh-brutal-yellow", to: "/nurses",   testid: "feature-portfolios" },
            { icon: MessageSquare, title: "Forums + DMs",         desc: "Ask questions. Share wins. Vent about night shift. Talk directly to nurses in your state.",                color: "nh-brutal-mint",   to: "/forums",   testid: "feature-forums" },
            { icon: Shield,      title: "Verified & Nurse-Owned", desc: "Every nurse is credential-verified. No recruiters, no spam, no data brokers. Ever.",                        color: "nh-brutal",        to: "/trust",    testid: "feature-verified" },
          ].map((f, i) => (
            <Link key={i} to={f.to} data-testid={f.testid} onClick={() => track("cta_click", { label: `landing_feature_${f.testid}` })}
              className={`p-6 bg-white ${f.color} nh-hover-lift block group`}>
              <f.icon className="w-8 h-8" strokeWidth={2.5} />
              <div className="mt-4 font-black text-xl group-hover:underline underline-offset-4">{f.title}</div>
              <p className="mt-2 text-sm text-black/70">{f.desc}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* PAY HEATMAP — renders only when backend has >=20 total submissions in >=5 states */}
      <PayHeatmap />

      {/* TESTIMONIALS — small, sincere, no fake photos */}
      <section className="border-t-2 border-black bg-[hsl(var(--nh-mint))]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16" data-testid="testimonials">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
            <div>
              <div className="text-xs uppercase tracking-widest text-black/60">What nurses are saying</div>
              <h2 className="mt-1 text-4xl sm:text-5xl font-black tracking-tighter">From the nurses using it.</h2>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { stars: 5, quote: "Finally a place made specifically for nurses.", by: "Sarah, RN" },
              { stars: 5, quote: "The agency directory alone saved me hours.", by: "Michael, LPN" },
              { stars: 5, quote: "I love that everyone is credential-verified. No recruiters. No spam.", by: "Jamie, RN" },
            ].map((t, i) => (
              <div key={i} className="bg-white nh-brutal p-6" data-testid={`testimonial-${i}`}>
                <div className="text-[hsl(var(--nh-red))] text-lg tracking-widest" aria-label={`${t.stars} out of 5 stars`}>
                  {"★".repeat(t.stars)}
                </div>
                <p className="mt-3 text-base font-black leading-snug">&ldquo;{t.quote}&rdquo;</p>
                <p className="mt-3 text-xs uppercase tracking-widest text-black/60">— {t.by}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SEO / FAQ content — visible and adds crawl-friendly context */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-16 border-t-2 border-black" data-testid="seo-content">
        <h2 className="text-3xl sm:text-4xl font-black tracking-tighter">What NurseLinkHub is (and isn&apos;t).</h2>
        <div className="mt-6 grid md:grid-cols-2 gap-6 text-black/80">
          <div>
            <h3 className="font-black text-lg">A verified network for U.S. nurses</h3>
            <p className="mt-2 text-sm">NurseLinkHub is a professional social platform built exclusively for licensed U.S. nurses. Every nurse is credential-verified — either automatically via the U.S. National Provider Identifier (NPI) Registry, or manually through admin-reviewed license and photo-ID uploads. No recruiters, no scrapers, no scam job posts.</p>
          </div>
          <div>
            <h3 className="font-black text-lg">A transparent agency directory</h3>
            <p className="mt-2 text-sm">Browse every active U.S. nursing agency in one place. Filter by state, RN or LPN specialty, travel or per-diem or 13-week contracts, and pay range. Click through to each agency&apos;s own application page and contact info — direct, no middleman.</p>
          </div>
          <div>
            <h3 className="font-black text-lg">A community that gets nursing</h3>
            <p className="mt-2 text-sm">Beyond the directory, NurseLinkHub is where nurses build a professional portfolio, follow peers, DM each other, discuss clinical practice in forums, host meetups and CE courses in events, and vote in community polls. It&apos;s LinkedIn meets a group chat, tuned for the 12-hour shift life.</p>
          </div>
          <div>
            <h3 className="font-black text-lg">Nurse-owned & independent</h3>
            <p className="mt-2 text-sm">NurseLinkHub is not owned by any agency, hospital system, or recruiter network. We&apos;re built and maintained by nurses. We make money only from nurse subscriptions. Every agency listing links out to that agency&apos;s own site.</p>
          </div>
        </div>

        <h2 className="mt-14 text-3xl font-black tracking-tighter">Common questions</h2>
        <div className="mt-6 space-y-4 text-black/80 text-sm">
          <div>
            <h3 className="font-bold text-base">Who can join?</h3>
            <p className="mt-1">Any U.S.-licensed <b>RN, LPN, BSN, MSN, DNP, APRN, NP,</b> or <b>CRNA</b> — plus <b>nursing students</b> enrolled in an accredited program. You can sign up in under a minute and verify your credentials afterward.</p>
          </div>
          <div>
            <h3 className="font-bold text-base">How is credential verification done?</h3>
            <p className="mt-1">Two paths: (1) instant automated verification via the CMS/HHS NPI Registry when your first and last name match the registered nurse on record; or (2) manual admin review of an uploaded license image plus optional government photo ID for a stricter identity check.</p>
          </div>
          <div>
            <h3 className="font-bold text-base">Do agencies pay to be listed?</h3>
            <p className="mt-1">No. Agencies list free. Listings are admin-reviewed to prevent scam postings. We make money only from nurse subscriptions.</p>
          </div>
          <div>
            <h3 className="font-bold text-base">Is this affiliated with any specific agency or hospital system?</h3>
            <p className="mt-1">No. NurseLinkHub is nurse-owned and independent. Every agency listing links out to that agency&apos;s own site.</p>
          </div>
        </div>
      </section>

      {/* Pricing strip — MOVED to bottom, kept low-key intentionally */}
      <section className="border-t-2 border-b-2 border-black bg-black text-white" data-testid="pricing-strip">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 grid lg:grid-cols-3 gap-8 items-center">
          <div className="lg:col-span-2">
            <p className="text-lg text-white/90 max-w-2xl leading-relaxed" data-testid="pricing-preamble">
              Join the only nurse-owned, verified network built exclusively for licensed U.S. nurses.
            </p>
            <p className="mt-3 text-sm text-white/60 max-w-2xl">
              After the Founding 30 is claimed, membership is a flat <span className="font-black text-white">$5 / month</span>. No tiers, no upsells, cancel any time.
            </p>
          </div>
          <div className="justify-self-end w-full lg:w-auto">
            <Link to="/register" data-testid="pricing-cta" onClick={() => track("cta_click", { label: "landing_pricing_cta" })}>
              <Button className="bg-[hsl(var(--nh-red))] hover:bg-[hsl(var(--nh-red-dark))] text-white rounded-none border-2 border-white h-14 px-8 text-lg w-full lg:w-auto">
                {remaining > 0 ? "Claim your Founding Nurse spot" : "Claim your verified profile"} <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
